/**
 * viewpointCamera.ts
 *
 * Pure camera-state <-> POI(legacy)-document conversion, shared by the
 * Viewpoints tab and BCF Issues. No React / useThree dependency, so it can
 * be used both inside the Canvas (capture) and outside it (building the
 * REST payload, inferring mode for documents loaded from the DB).
 *
 * The legacy POI schema (Position/Rotation/Euler/Pivot/Armlength/Scale) is
 * also read by the Unity viewer and the Unreal Pixel Streaming viewer, so it
 * keeps being written exactly as before — Quaternion is the only additive
 * field; topdown/orbit/perspective_topdown are told apart purely by
 * geometry (see inferCameraMode below), no extra discriminator field needed.
 * See /home/andreas/.claude/plans/refactored-humming-steele.md
 */

import * as THREE from 'three';
import { ZERO_SCENE_OFFSET } from 'common/sceneOffset';
import type { SceneOffset } from 'common/sceneOffset';
import type { CameraMode } from '../stores/useCameraStore';

export interface ViewpointCameraState {
  cameraMode: CameraMode;
  position: THREE.Vector3;
  /** walk only */
  quaternion?: THREE.Quaternion;
  /** modelview / topdown / perspective_topdown only — the orbit target (= legacy Pivot) */
  target?: THREE.Vector3;
  /** modelview / topdown / perspective_topdown only — camera-to-target distance (= legacy Armlength) */
  distance?: number;
}

export interface LegacyViewpointFields {
  Position: { x: number; y: number; z: number };
  Rotation: { x: number; y: number; z: number };
  Euler: { x: number; y: number; z: number };
  Pivot: { x: number; y: number; z: number };
  Armlength: number;
  Scale: number;
}

const STRAIGHT_DOWN_TOLERANCE_DEG = 1;
// cos() because Rotation.y for orbit-like modes is the y-component of the
// normalized pivot->camera direction, i.e. cos(angle from straight up/down).
const STRAIGHT_DOWN_COS_THRESHOLD = Math.cos(THREE.MathUtils.degToRad(STRAIGHT_DOWN_TOLERANCE_DEG));

/** Old A-Frame "modelview" zoom buckets — Unity/Unreal/Pixel-Streaming still read this. */
function scaleForDistance(distance: number): number {
  return distance < 30 ? 10 : 50;
}

/**
 * Normalized camera state -> the legacy POI fields every viewer (R3F, Unity,
 * Unreal Pixel Streaming) understands. Always populated, regardless of
 * camera mode, so old viewers keep working unmodified.
 */
/** Treats an all-zero (or missing) vector as "not set" — the old editor's `vpEulerNull`
 * check and the Unity app's `poi.Euler.sqrMagnitude > 0.001f` do exactly the same. A
 * viewpoint that genuinely looks along -Z with no pitch is indistinguishable from an
 * unset one in this schema; it resolves correctly anyway via the Rotation fallback. */
function isSetVec(v?: { x?: number; y?: number; z?: number } | null): boolean {
  if (!v) return false;
  return Math.abs(v.x ?? 0) + Math.abs(v.y ?? 0) + Math.abs(v.z ?? 0) > 1e-6;
}

/**
 * The pre-Euler `Rotation` field -> a camera quaternion.
 *
 * Two writers have given this field two different meanings, and both kinds of
 * record are still in the database:
 *
 *  - The old editor and VR app wrote a unit DIRECTION vector
 *    `{ -sin(yaw)cos(pitch), sin(pitch), cos(yaw)cos(pitch) }` — i.e. the camera
 *    forward vector with its Z flipped, the same flip the Position field uses.
 *    Records written by the editor always have pitch 0 (getBCFLocationData only
 *    ever produced a floor-plane heading), so they look like `{ -sin, 0, cos }`;
 *    records from the VR app carry a real pitch in `y`. bcf-system.js decoded
 *    only the heading, via `Vector2(-x, -z).angle() + PI/2`.
 *  - The current Unity app writes A-Frame EULER ANGLES IN RADIANS here, alongside
 *    the same angles in degrees in `Euler` (POIMapper.MapToBaseDto /
 *    CoordinateSystemConverter.ToAframeCameraRadians).
 *
 * The two are told apart by length: a direction vector is a unit vector, an euler
 * triple almost never is. Note this must test the FULL vector, not just (x, z) —
 * a pitched direction vector has `hypot(x, z) === cos(pitch)`, which is not 1.
 * Unity records reach this path only if their `Euler` is unset, which does not
 * happen in practice — it always writes both.
 */
export function quaternionFromLegacyRotation(rot: { x?: number; y?: number; z?: number }): THREE.Quaternion {
  const x = rot.x ?? 0;
  const y = rot.y ?? 0;
  const z = rot.z ?? 0;

  const isDirectionVector = Math.abs(Math.hypot(x, y, z) - 1) < 1e-3;
  const euler = isDirectionVector
    ? new THREE.Euler(
        // Heading comes from the horizontal part — atan2 is scale-invariant, so
        // the cos(pitch) factor on x and z drops out and this stays correct for
        // both the flat and the pitched form.
        Math.asin(THREE.MathUtils.clamp(y, -1, 1)),
        Math.atan2(-z, -x) + Math.PI / 2,
        0,
        'YXZ',
      )
    : new THREE.Euler(x, y, 0, 'YXZ'); // Unity: A-Frame euler, radians

  return new THREE.Quaternion().setFromEuler(euler);
}

// Position/Pivot.z is inverted on the way out (and back in cameraStateFromLegacyFields)
// because the legacy schema's Z axis is flipped relative to R3F's, so Unity/Unreal/
// Pixel-Streaming viewers can keep consuming these fields unchanged.
//
// `offset` is the pipeline's centering translation T (see common/sceneOffset.ts).
// The legacy schema stores camera positions in the scene's ORIGINAL, un-centered
// frame, so T is subtracted here and added back in cameraStateFromLegacyFields.
// It is {0,0,0} for every scene the pipeline did not center (all floorplanner
// scenes), which makes this a no-op there.
//
// The Y sign differs per mode on purpose: walk stores `y - T.y` while the
// orbit-like modes store `y + T.y`. That asymmetry is baked into the existing
// data and into the Unreal Navigator (PStreamingUI.goToViewpoint adds +T.y for
// walk and -T.y for modelview), so it has to be reproduced, not tidied up.
export function buildLegacyFields(
  state: ViewpointCameraState,
  offset: SceneOffset = ZERO_SCENE_OFFSET,
): LegacyViewpointFields {
  const { cameraMode, position, quaternion, target, distance } = state;

  if (cameraMode === 'walk') {
    const euler = new THREE.Euler().setFromQuaternion(quaternion ?? new THREE.Quaternion(), 'YXZ');
    return {
      Position: {
        x: position.x - offset.x,
        y: position.y - offset.y,
        z: -(position.z - offset.z),
      },
      // Legacy unit direction vector — kept alongside Euler/Quaternion for
      // readers that predate them. This is the camera forward vector with Z
      // flipped, which is what both remaining decoders expect:
      // quaternionFromLegacyRotation() above, and PStreamingUI.goToViewpoint
      // (`atan2(-x, -z)` for heading, `asin(y)` for pitch). Writing cos(yaw)
      // into `y` instead would read back there as a 90-degree pitch — i.e.
      // staring at the ceiling. Carrying the pitch here (rather than the flat
      // yaw-only form the old editor wrote) costs nothing: the yaw-only
      // decoders ignore `y`, and it reduces to their form when pitch is 0.
      Rotation: {
        x: Math.sin(-euler.y) * Math.cos(euler.x),
        y: Math.sin(euler.x),
        z: Math.cos(euler.y) * Math.cos(euler.x),
      },
      Euler: {
        x: THREE.MathUtils.radToDeg(euler.x),
        y: THREE.MathUtils.radToDeg(euler.y),
        z: THREE.MathUtils.radToDeg(euler.z),
      },
      Pivot: { x: 0, y: 0, z: 0 },
      Armlength: 0,
      Scale: 1,
    };
  }

  // modelview / topdown / perspective_topdown all share the old "modelview"
  // shape: Pivot + Armlength + the normalized pivot->camera direction. For
  // topdown/perspective_topdown this direction comes out as (~0, ~1, ~0)
  // naturally, since the camera is locked directly above the target — no
  // special-casing needed.
  const pivot = target ?? new THREE.Vector3();
  const dir = position.clone().sub(pivot);
  const dist = distance ?? dir.length();
  if (dir.lengthSq() > 0) dir.normalize();

  return {
    Position: {
      x: position.x - offset.x,
      y: position.y + offset.y,
      z: -(position.z - offset.z),
    },
    Rotation: { x: dir.x, y: dir.y, z: dir.z },
    Euler: { x: 0, y: 0, z: 0 },
    Pivot: {
      x: pivot.x - offset.x,
      y: pivot.y + offset.y,
      z: -(pivot.z - offset.z),
    },
    Armlength: dist,
    Scale: scaleForDistance(dist),
  };
}

/**
 * Resolves which camera mode a viewpoint should restore into, purely from
 * the legacy geometry — no separate CameraMode field needed. `orbit`/
 * `modelview`'s polar angle is kept clamped a few degrees clear of vertical
 * in CameraRig.tsx specifically so a real orbit shot can never land inside
 * the straight-down tolerance band and get misclassified as topdown.
 *
 * There are only three real, user-selectable viewing modes: walk, modelview
 * (= orbit), and perspective_topdown. The old 2D top-down *viewing* mode is
 * discontinued — 'topdown' as a CameraMode value still exists, but only as
 * plan-mode's internal camera angle, never as something a viewpoint should
 * resolve into. So the straight-down geometry case below resolves to
 * perspective_topdown, not topdown.
 */
export function inferCameraMode(doc: {
  Scale?: number | null;
  Rotation?: { x?: number; y?: number; z?: number } | null;
}): CameraMode {
  if (!doc.Scale || doc.Scale <= 1) return 'walk';
  if (doc.Rotation && (doc.Rotation.y ?? 0) >= STRAIGHT_DOWN_COS_THRESHOLD) return 'perspective_topdown';
  return 'modelview';
}

/**
 * Legacy POI document -> normalized camera state, for navigation.
 *
 * `offset` is the pipeline's centering translation T — see buildLegacyFields
 * above for why it is added back here, and why its Y sign is mode-dependent.
 * Passing it is what makes viewpoints of old, upload-based (centered) scenes
 * land inside the room instead of below and beside it.
 */
export function cameraStateFromLegacyFields(doc: {
  Position?: { x?: number; y?: number; z?: number } | null;
  Pivot?: { x?: number; y?: number; z?: number } | null;
  Armlength?: number | null;
  Euler?: { x?: number; y?: number; z?: number } | null;
  Quaternion?: { x?: number; y?: number; z?: number; w?: number } | null;
  Scale?: number | null;
  Rotation?: { x?: number; y?: number; z?: number } | null;
}, offset: SceneOffset = ZERO_SCENE_OFFSET): ViewpointCameraState {
  const cameraMode = inferCameraMode(doc);
  // Walk adds +T.y, the orbit-like modes -T.y (see buildLegacyFields).
  const offsetY = cameraMode === 'walk' ? offset.y : -offset.y;
  const position = new THREE.Vector3(
    (doc.Position?.x ?? 0) + offset.x,
    (doc.Position?.y ?? 0) + offsetY,
    -(doc.Position?.z ?? 0) + offset.z,
  );

  if (cameraMode === 'walk') {
    // Quaternion (R3F editor only) -> Euler in degrees (old editor, Unity app)
    // -> Rotation (everything older). Viewpoints saved before Euler existed —
    // which is most of them in upload-based scenes — carry Rotation only, or
    // an all-zero Euler placeholder the old editor initialised and never
    // overwrote. Without the last branch those resolve to `undefined`, and
    // useCameraTransition then leaves the camera pointing wherever it already
    // was: every viewpoint appears to have the same rotation. The old editor
    // (ScenesProvider.goToViewpoint's `vpEulerNull` check), the Navigator and
    // the Unity app all still carry this same fallback.
    let quaternion: THREE.Quaternion | undefined;
    if (doc.Quaternion) {
      quaternion = new THREE.Quaternion(doc.Quaternion.x, doc.Quaternion.y, doc.Quaternion.z, doc.Quaternion.w);
    } else if (isSetVec(doc.Euler)) {
      const euler = new THREE.Euler(
        THREE.MathUtils.degToRad(doc.Euler!.x ?? 0),
        THREE.MathUtils.degToRad(doc.Euler!.y ?? 0),
        0,
        'YXZ',
      );
      quaternion = new THREE.Quaternion().setFromEuler(euler);
    } else if (isSetVec(doc.Rotation)) {
      quaternion = quaternionFromLegacyRotation(doc.Rotation!);
    }
    return { cameraMode, position, quaternion };
  }

  const target = new THREE.Vector3(
    (doc.Pivot?.x ?? 0) + offset.x,
    (doc.Pivot?.y ?? 0) + offsetY,
    -(doc.Pivot?.z ?? 0) + offset.z,
  );
  const distance = doc.Armlength ?? position.distanceTo(target);
  return { cameraMode, position, target, distance };
}
